import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { v4 as uuidv4 } from 'uuid';

import { ReactComponent as ShowStatisticsSvg } from '@assets/svg/ic-show-stats.svg';
import { ReactComponent as EqSavingsSvg } from '@assets/svg/metrics-icons/ic-equipmentSavings.svg';
import { ReactComponent as TotalReportsSvg } from '@assets/svg/metrics-icons/ic-totalReports.svg';
import { ReactComponent as TotalSavingsSvg } from '@assets/svg/metrics-icons/ic-totalSavings.svg';
import { ReactComponent as UnplannedDowntimeSvg } from '@assets/svg/metrics-icons/ic-unplannedDowntime.svg';

import { toCurrency } from '@helpers/helper';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useCustomerInfoQuery } from '@services/useCustomerInfoQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import TotalStatisticsSkeleton from './StatisticsBlockSkeleton';
import style from './style.module.scss';

const StatisticsBlock = () => {
  const { t } = useTranslation();
  const locationSelectValue = selectAppLocationSelectValue();
  const { customerId } = useParams();
  const { pathname } = useLocation();

  const { data: customerInfoData, isSuccess: isCustomerInfoSuccess } = useCustomerInfoQuery({
    customerId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
  });

  const { data: assetTreeData } = useAssetTreeQuery();

  const isSelectedAllFacility = locationSelectValue.id === 'all';

  const currentCurrency = isSelectedAllFacility
    ? assetTreeData?.customerCurrency
    : locationSelectValue.currency;

  const options = isCustomerInfoSuccess
    ? [
        {
          allowedTo: customerInfoData.showUnplannedDowntime,
          icon: <UnplannedDowntimeSvg className={style.statItemIcon} />,
          infoTotal: t('Unplanned Downtime Hours Avoided, YTD'),
          infoQuantity: `${toCurrency(customerInfoData?.downtimeAvoided)} ${t('hrs')}`,
        },
        {
          allowedTo: customerInfoData.showLphAvoided,
          icon: <UnplannedDowntimeSvg className={style.statItemIcon} />,
          infoTotal: t('LPH Avoided, YTD'),
          infoQuantity: `${toCurrency(customerInfoData?.lphAvoided)} ${t('hrs')}`,
        },
        {
          allowedTo: true,
          icon: <EqSavingsSvg className={style.statItemIcon} />,
          infoTotal: `${t('Equipment/Labor Savings')}, ${t('YDT')}`,
          infoQuantity: `${currentCurrency.symbol} ${toCurrency(customerInfoData?.equipmentLaborSaving)}`,
        },
        {
          allowedTo: customerInfoData.showTotalCostSavings,
          icon: <TotalSavingsSvg className={style.statItemIcon} />,
          infoTotal: t('Total Cost Savings, YTD'),
          infoQuantity: `${currentCurrency.symbol} ${toCurrency(customerInfoData?.costAvoided)}`,
        },
        {
          allowedTo: customerInfoData.showCompletionReports,
          icon: <TotalReportsSvg className={style.statItemIcon} />,
          infoTotal: `${t('Total Completion Reports Submitted')}, ${t('YDT')}`,
          infoQuantity: customerInfoData?.countReports || 0,
        },
      ]
    : [];

  return (
    <div className={style.statisticsBlockWrapper}>
      <div className={style.statisticsBlock} id="tourStep-5">
        {options.length ? (
          options.map(
            (option) =>
              option.allowedTo && (
                <div key={uuidv4()} className={style.statItem}>
                  <div className={style.statItemInfo}>
                    <div className={style.statItemText}>{option.infoTotal}</div>
                    <div className={style.statItemQuantity}>{option.infoQuantity}</div>
                  </div>
                  {option.icon}
                </div>
              ),
          )
        ) : (
          <TotalStatisticsSkeleton />
        )}
      </div>
      <div className={style.statsIconWrapper} id="tourStep-6">
        <Link
          to={`/customer/${customerId}/executive-summary/cost-savings-details`}
          state={{ prevPage: pathname }}
        >
          <ShowStatisticsSvg
            className={style.statsIcon}
            data-tooltip-id="header-stat-item"
            data-tooltip-content={t('View more statistics')}
          />
        </Link>
      </div>
      <Tooltip id="header-stat-item" className={style.statisticsTooltip} />
    </div>
  );
};

export default StatisticsBlock;
